import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { TranslateOrDefault } from '@language';
import {
    BaseContainer,
    DottedKeyToFlatKey,
    FacetSelectionType,
    ShFacet,
    ShSelectableFacetValue,
    ShSelectedFacetValue
} from '@stylehaven/components';
import isEqual from 'lodash-es/isEqual';
import { Facet } from '../../../common/entities/mediahaven-api/facets/facet.entity';
import { toSelectableFacetValue } from '../../../dialog/helpers/selectable-facet-value.helper';

@Component({
    standalone: false,
    selector: 'med-filters',
    templateUrl: './filters.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersComponent extends BaseContainer {
    private readonly facets$: ReplaySubject<Facet[]> = new ReplaySubject(1);
    private readonly selectedFacetValuesRaw$: BehaviorSubject<ShSelectedFacetValue[]> = new BehaviorSubject<
        ShSelectedFacetValue[]
    >([]);

    public facetsLoading$: ReplaySubject<boolean> = new ReplaySubject(1);
    public selectedFacets$: Observable<ShSelectedFacetValue[]>;
    public shFacets$: Observable<ShFacet[]>;

    @Input() set facets(facets: Facet[] | null) {
        if (facets !== null) {
            this.facets$.next(facets);
            this.facetsLoading$.next(false);
        }
    }

    @Input() set facetsLoading(isLoading: boolean | null) {
        this.facetsLoading$.next(isLoading === true);
    }

    @Input() set selected(facetValues: ShSelectedFacetValue[] | null) {
        if (facetValues !== null) {
            // MED-2197: Translate selected facets from external sources
            const translatedFacetValues: ShSelectedFacetValue[] = this.translateSelectedFacetValues(facetValues);
            this.selectedFacetValuesRaw$.next(translatedFacetValues);
        }
    }

    @Input() blockShowMore = false;

    @Output() facetValueChanged: EventEmitter<ShSelectedFacetValue[]> = new EventEmitter<ShSelectedFacetValue[]>();
    @Output() facetLoadMore: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();

    constructor() {
        super();
        this.setupStreams();
    }

    // Component is currently only used for tasks, so selectionType can be hardcoded to 'MUST'
    private setupStreams(): void {
        this.shFacets$ = combineLatest([this.selectedFacetValuesRaw$, this.facets$]).pipe(
            map(([selectedFacetValues, facets]) =>
                facets.map(facet => this.convertFacetValues(selectedFacetValues, facet))
            )
        );

        this.selectedFacets$ = this.selectedFacetValuesRaw$;
    }

    private convertFacetValues(selectedFacetValues: ShSelectedFacetValue[], facet: Facet): ShFacet {
        const selectableValues: ShSelectableFacetValue[] = facet.Values.map(facetValue => {
            const index: number = selectedFacetValues.findIndex(
                qp => qp.key === facet.DottedKey && isEqual(qp.value, facetValue.Value)
            );
            return toSelectableFacetValue(
                facetValue.Value,
                facetValue.Label,
                FacetSelectionType.MUST,
                facetValue.Count,
                facetValue.Missing,
                index > -1
            );
        });

        const unselectedOnly: ShSelectableFacetValue[] = selectableValues.filter(facetValue => !facetValue.selected);

        return {
            key: facet.DottedKey,
            label: facet.Label,
            selectionType: FacetSelectionType.MUST,
            values: unselectedOnly,
            hasMoreValues: facet.Count > facet.Values.length
        };
    }

    public onFacetSelected(changedFacetValue: ShSelectedFacetValue): void {
        combineLatest([this.selectedFacetValuesRaw$, this.facets$])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([selectedFacets, facets]) => {
                const selectedFacetIndex: number = selectedFacets.findIndex(selectedFacet =>
                    isEqual(selectedFacet, changedFacetValue)
                );

                if (selectedFacetIndex > -1) {
                    const elRemoved: ShSelectedFacetValue[] = selectedFacets.filter(
                        (el, index) => index !== selectedFacetIndex
                    );
                    this.facetValueChanged.emit(elRemoved);
                    return;
                }

                // remove previous if nested facet, currently defined by having the GAP property set. (WE NEED FACET TYPES)
                const facet: Facet | undefined = facets.find(
                    facetInfo => changedFacetValue.key === facetInfo.DottedKey
                );
                const isNested: boolean = !!facet && Object.prototype.hasOwnProperty.call(facet.Values[0], 'Gap');

                const oldRemoved: ShSelectedFacetValue[] = isNested
                    ? selectedFacets.filter(selectedFacet => selectedFacet.key !== changedFacetValue.key)
                    : selectedFacets;

                const newState: ShSelectedFacetValue[] = [...oldRemoved, changedFacetValue];
                this.facetValueChanged.emit(newState);
            });
    }

    private translateSelectedFacetValues(facetValues: ShSelectedFacetValue[]): ShSelectedFacetValue[] {
        return facetValues.map(facetValue => ({
            ...facetValue,
            valueLabel: TranslateOrDefault(
                this.translateService,
                `overrides.${DottedKeyToFlatKey(facetValue.key)}.${facetValue.value}`,
                facetValue.valueLabel
            )
        }));
    }

    public onClear(): void {
        this.facetValueChanged.emit([]);
    }

    public onRemoveFacet(unselectedFacetValue: ShSelectedFacetValue): void {
        this.selectedFacetValuesRaw$.pipe(take(1), takeUntil(this.destroy$)).subscribe(selectedFacets => {
            this.facetValueChanged.emit(
                selectedFacets.filter(selectedFacetValue => !isEqual(selectedFacetValue, unselectedFacetValue))
            );
        });
    }

    public onLoadMore(facet: ShFacet): void {
        this.facetLoadMore.emit(facet);
    }
}
